// Financing waiting to fund (BL-005), in Receivables: each financed sale the lender still owes on,
// oldest first, with its loan contract and what's owed; "Record payout" from the lender's funding
// statement (what arrived in the bank and its dealer fee; a holdback stays owed for a later payout).
// Nothing shows when nothing waits.
import { useState } from 'react';
import { newId } from '../shared/ids.ts';
import { leftWords, payoutProblem, type FinancingWaiting as Row } from './financing-model.ts';
import { dayText, parseMoney, todayIn } from './model.ts';
import type { BankAccount } from './types.ts';
import { Money, Panel, useAct, useQuery } from './ui.tsx';

export function FinancingWaiting({
  toast,
  timeZone,
  refresh,
}: {
  toast: (m: string) => void;
  timeZone?: string;
  /** Changes after a payment is recorded here, so a financed one shows at once. */
  refresh: number;
}) {
  const waiting = useQuery<{ payments: Row[] }>('payments.financingWaiting', {}, [refresh]);
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const [open, setOpen] = useState<string | null>(null);
  const rows = waiting.data?.payments ?? [];
  if (!rows.length) return null;
  return (
    <Panel title="Financing waiting to fund">
      <ul className="rows">
        {rows.map((r) => (
          <li key={r.paymentId}>
            <div className="inv-row">
              <span>
                <strong>{r.customerName}</strong>{' '}
                <span className="muted small">
                  loan {r.reference} · signed {dayText(r.receivedOn)} · sale <Money cents={r.amountCents} />
                  {r.fundedCents > 0 && (
                    <>
                      {' '}
                      · paid out <Money cents={r.fundedCents} />
                    </>
                  )}
                </span>
              </span>
              <span>
                owed <Money cents={r.owedCents} />
              </span>
              <button
                className="btn quiet"
                onClick={() => setOpen(open === r.paymentId ? null : r.paymentId)}
              >
                Record payout
              </button>
            </div>
            {open === r.paymentId && (
              <PayoutForm
                row={r}
                banks={banks.data?.bankAccounts ?? []}
                today={todayIn(timeZone)}
                toast={toast}
                onDone={(recorded) => {
                  setOpen(null);
                  if (recorded) void waiting.reload();
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function PayoutForm({
  row,
  banks,
  today,
  toast,
  onDone,
}: {
  row: Row;
  banks: readonly BankAccount[];
  today: string;
  toast: (m: string) => void;
  onDone: (recorded: boolean) => void;
}) {
  const act = useAct(toast, () => undefined);
  const [funded, setFunded] = useState((row.owedCents / 100).toFixed(2));
  const [fee, setFee] = useState('0.00');
  const [on, setOn] = useState(today);
  const [bank, setBank] = useState(banks[0]?.id ?? '');
  const [reference, setReference] = useState('');
  // one payout, one id: pressing twice records it once
  const [fundingId] = useState(newId);
  const fundedCents = parseMoney(funded);
  const feeCents = parseMoney(fee);
  const problem =
    payoutProblem(fundedCents, feeCents, row.owedCents) ?? (bank ? null : 'Choose the bank it arrived in.');
  return (
    <form
      className="fgrid inline-form"
      aria-label={`Record a payout for ${row.customerName}`}
      onSubmit={(e) => e.preventDefault()}
    >
      <label>
        <span className="flabel">Paid into the bank</span>
        <input
          className="field"
          inputMode="decimal"
          value={funded}
          onChange={(e) => setFunded(e.target.value)}
        />
      </label>
      <label>
        <span className="flabel">Lender’s fee</span>
        <input className="field" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />
      </label>
      <label>
        <span className="flabel">Paid out on</span>
        <input className="field" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      </label>
      <label>
        <span className="flabel">Bank</span>
        <select className="field" value={bank} onChange={(e) => setBank(e.target.value)}>
          {banks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
              {b.last4 ? ` ·${b.last4}` : ''}
            </option>
          ))}
        </select>
      </label>
      <label className="wide">
        <span className="flabel">Funding statement reference (optional)</span>
        <input className="field" value={reference} onChange={(e) => setReference(e.target.value)} />
      </label>
      <p className={`full small${problem ? ' bad-ink' : ' muted'}`} role={problem ? 'alert' : 'status'}>
        {problem ?? `${leftWords(row.owedCents, fundedCents!, feeCents!)}.`}
      </p>
      <div className="row-actions">
        <button
          className="btn primary"
          type="button"
          disabled={act.busy || !!problem}
          onClick={() =>
            void (async () => {
              const ok = await act.run(
                'payments.recordFinancingFunded',
                {
                  paymentId: row.paymentId,
                  fundingId,
                  fundedCents: fundedCents!,
                  feeCents: feeCents!,
                  fundedOn: on,
                  bankAccountId: bank,
                  ...(reference.trim() ? { reference: reference.trim() } : {}),
                },
                'Payout recorded',
              );
              if (ok) onDone(true);
            })()
          }
        >
          Record payout
        </button>
        <button className="btn quiet" type="button" onClick={() => onDone(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
